import type { ChatConversationSummary } from '@mogzo/shared';
import { BellOff } from 'lucide-react';
import { Link } from 'react-router';
import { useMinute } from '../../lib/chat';
import { shortAgo } from '../../lib/notifications';
import { ChatAvatar } from './parts';

function lastLine(chat: ChatConversationSummary) {
  if (!chat.last) return chat.kind === 'group' ? 'No messages yet' : '';
  if (chat.last.fromMe) return `You: ${chat.last.text}`;
  return chat.kind === 'group' ? `${chat.last.from}: ${chat.last.text}` : chat.last.text;
}

/**
 * The chats in the inbox, newest first: who, the last message and when, and how many are
 * unread. Each opens with `onOpen` (the chat window) or by link (Messages as a page).
 */
export function ConversationList({
  chats,
  current,
  onOpen,
  linkTo,
}: {
  chats: ChatConversationSummary[];
  current?: string | null;
  onOpen?: (id: string) => void;
  linkTo?: (id: string) => string;
}) {
  useMinute();
  return (
    <ul className="m-0 flex list-none flex-col p-0">
      {chats.map((chat) => {
        const unread = chat.unread > 0 && !chat.muted;
        const body = (
          <>
            <ChatAvatar chat={chat} />
            <span className="min-w-0 flex-1 leading-[19px]">
              <b className="flex items-center gap-1.5 font-semibold">
                <span className="truncate">{chat.name}</span>
                {chat.muted && (
                  <BellOff
                    size={14}
                    strokeWidth={1.9}
                    aria-label="Muted"
                    className="shrink-0 text-muted"
                  />
                )}
              </b>
              <span
                className={`block truncate text-sm ${unread || chat.status === 'request' ? 'font-semibold text-ink' : 'text-muted'}`}
              >
                {lastLine(chat)}
              </span>
            </span>
            <span className="flex shrink-0 flex-col items-end gap-1.5 self-stretch pt-0.5">
              {chat.last && <span className="text-xs text-subtle">{shortAgo(chat.last.at)}</span>}
              {unread && (
                <span className="grid h-5 min-w-5 place-items-center rounded-full bg-brand px-1.5 text-[11px] font-bold text-ink">
                  {chat.unread}
                  <span className="sr-only"> unread</span>
                </span>
              )}
            </span>
          </>
        );
        const className = `flex w-full items-center gap-3 rounded-[14px] p-2.5 text-left hover:bg-field ${current === chat.id ? 'bg-field' : ''}`;
        return (
          <li key={chat.id}>
            {linkTo ? (
              <Link
                to={linkTo(chat.id)}
                aria-current={current === chat.id ? 'page' : undefined}
                className={className}
              >
                {body}
              </Link>
            ) : (
              <button type="button" onClick={() => onOpen?.(chat.id)} className={className}>
                {body}
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
